import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Injector,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import {
  NavigationEnd,
  NavigationError,
  Router,
  Event as RouterEvent,
  RouterLink,
  RouterLinkActive,
  RouterOutlet,
} from '@angular/router';
import { AuthService } from '@pinnakl/auth/providers';
import { AuthRecoveryPanelComponent } from '@pinnakl/auth/shared';
import { AuthPopupService } from '@pinnakl/authentication';
import { CurrentUserStore } from '@pinnakl/core/data-providers';
import { EnvironmentService } from '@pinnakl/core/environment';
import { AgGridLicenseService } from '@pinnakl/grid/shared';
import { AumStore } from '@pinnakl/poems/aum/data-access';
import { PoemsSidebarStore } from '@pinnakl/poems/data-access';
import { PriceEventsStreamStore } from '@pinnakl/poems/streams/price/data-access';
import { TradeWorkflowSpecStore } from '@pinnakl/poems/trade-workflow-spec/data-access';
import { DefaultConfirmActionComponent, ReconnectWindowComponent } from '@pinnakl/shared-ui';
import { PinnaklGlobalSpinnerComponent } from '@pinnakl/shared/base-components';
import { HeaderUserProfileComponent } from '@pinnakl/shared/domain-components';
import { PinnaklIconComponent } from '@pinnakl/shared/ui/icons';

import { PrimeButtonComponent } from '@pinnakl/shared/ui/prime';
import { AmplitudeEventsCollector, GrowthBookService } from '@pinnakl/shared/util-external-services';
import {
  injectInitialNavigationPending,
  NavigationService,
  PinnaklSpinnerService,
  PinnaklUIToastMessage,
} from '@pinnakl/shared/util-providers';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { ConfirmPopup } from 'primeng/confirmpopup';
import { Toast } from 'primeng/toast';
import { NavBarService } from './nav.bar.service';

@Component({
  selector: 'pnkl-frontend-root',
  templateUrl: 'app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    PinnaklIconComponent,
    AuthRecoveryPanelComponent,
    PrimeButtonComponent,
    PinnaklGlobalSpinnerComponent,
    ReconnectWindowComponent,
    DefaultConfirmActionComponent,
    RouterOutlet,
    RouterLinkActive,
    RouterLink,
    ConfirmPopup,
    ConfirmDialog,
    Toast,
    HeaderUserProfileComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [NavBarService],
  host: {
    '(window:keydown)': 'onKeyPress($event)',
  },
})
export class AppComponent implements OnInit, AfterViewInit {
  private readonly tradeWorkflowSpecStore = inject(TradeWorkflowSpecStore);
  private readonly navBarService = inject(NavBarService, { self: true });
  private readonly authService = inject(AuthService);
  private readonly authPopupService = inject(AuthPopupService);
  private readonly agGridLicenceService = inject(AgGridLicenseService);
  private readonly router = inject(Router);
  private readonly toastr = inject(PinnaklUIToastMessage);
  private readonly spinner = inject(PinnaklSpinnerService);
  private readonly title = inject(Title);
  private readonly envService = inject(EnvironmentService);
  private readonly aumStore = inject(AumStore);
  private readonly priceEventsStreamStore = inject(PriceEventsStreamStore);
  private readonly sidebarStore = inject(PoemsSidebarStore);

  private readonly injector: Injector = inject(Injector);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly currentUserStore = inject(CurrentUserStore);
  private readonly amplitudeEventsCollector = inject(AmplitudeEventsCollector);
  private readonly growthBookService = inject(GrowthBookService);
  private readonly navigationService: NavigationService = inject(NavigationService);

  protected readonly tradeWorkflowSpec = this.tradeWorkflowSpecStore.spec;
  protected readonly isPmsAccessLevelFour = computed(() => {
    const tradeWorkflowSpec = this.tradeWorkflowSpec();
    if (tradeWorkflowSpec) {
      return tradeWorkflowSpec.pmsAccessLevel === 4;
    }
    return false;
  });
  protected readonly emsV3Visible = this.tradeWorkflowSpecStore.hasEmsV3Access;
  readonly menuOpen = this.sidebarStore.menuOpen;
  protected readonly reportingActive = signal(false);
  protected readonly shadowNavFlagOn = computed(() => this.growthBookService.isOn('poems-shadow-nav'));
  protected readonly apiPlaygroundFlagOn = computed(() => this.growthBookService.isOn('poems-api-playground'));
  protected readonly emsV3FlagOn = computed(() => this.growthBookService.isOn('poems-ems-v3'));

  includeTesting = false;
  userIsAvailable = signal(false);
  protected readonly chromeVisible = computed(
    () => this.userIsAvailable() && !!this.currentUserStore.currentUserInfo(),
  );
  protected readonly initialNavigationPending = injectInitialNavigationPending();
  isLocal = false;
  currentUser = this.currentUserStore.currentUser;

  constructor() {
    this.currentUserStore.removeUser();
    this.authService.init();
    this.includeTesting = this.envService.get('includeTesting');
    this.isLocal = window.location.origin.includes('localhost');
  }

  ngOnInit(): void {
    this.navigationService.startSaveHistory().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    const effectRef = effect(
      () => {
        const currentUserInfo = this.currentUserStore.currentUserInfo();
        if (currentUserInfo) {
          this.userIsAvailable.set(true);
          this.setupAmplitude();
          this.growthBookService.updateAttributes({
            userId: currentUserInfo.user.id,
            clientId: currentUserInfo.user.clientId,
          });
          if (currentUserInfo.user.passwordResetRequired) this.openChangePasswordModal().then();
          this.tradeWorkflowSpecStore.loadTradeWorkflowSpec().then();
          effectRef.destroy();
        }
      },
      {
        injector: this.injector,
        manualCleanup: true,
      },
    );
  }

  ngAfterViewInit(): void {
    this.agGridLicenceService.initialize();
    this.router.events.subscribe((event: RouterEvent) => {
      this.navigationInterceptor(event);
    });
  }

  searchModalOpen(): void {
    this.navBarService.searchModalOpen();
  }

  logout(): void {
    this.spinner.spin();
    this.router.navigateByUrl('/redirect').then(() => {
      this.authService.logout();
    });
  }

  showIncompleteScreens(): boolean {
    let show = false;
    const user = this.currentUserStore.currentUser();
    if (user && (user.clientId === 2 || user.clientId === 3)) {
      show = true;
    }
    return show;
  }

  navigationInterceptor(event: RouterEvent): void {
    if (event instanceof NavigationEnd) {
      this.changeTitle(event);

      const { urlAfterRedirects } = event;
      const urlToCheck = urlAfterRedirects[0] === '/' ? urlAfterRedirects.slice(1) : urlAfterRedirects;
      this.sidebarStore.setMenuOpenOnNavigation(urlToCheck === 'dashboard');
      this.reportingActive.set(urlToCheck.startsWith('reporting/'));
    }
    if (event instanceof NavigationError) {
      console.error('Event error', event.error);
      this.toastr.error('An unexpected error occurred');
    }
  }

  toggleMenu(): void {
    this.sidebarStore.toggleMenu();
  }

  onKeyPress($event: KeyboardEvent): void {
    if (($event.ctrlKey || $event.metaKey) && $event.code === 'KeyS') {
      $event.preventDefault();
      $event.stopImmediatePropagation();
      this.navBarService.searchModalOpen();
      this.aumStore.loadAums();
      this.priceEventsStreamStore.subscribeToStream();
    }
  }

  async openChangePasswordModal(): Promise<void> {
    const password = await this.authPopupService.openChangePasswordModal();
    if (!password) return;

    this.spinner.spin();
    try {
      await this.currentUserStore.changePassword(password);
      this.authService.passwordResetDone();
      this.toastr.success('Password changed successfully!');
    } finally {
      this.spinner.stop();
    }
  }

  private setupAmplitude(): void {
    const isDebugLogsMode = localStorage.getItem('amplitudeEventsCollectingMode') === 'true';
    this.amplitudeEventsCollector.initialize(
      this.currentUserStore.currentUserInfo().user.email,
      this.currentUserStore.currentUserInfo().user.clientId,
      isDebugLogsMode,
    );
  }

  private changeTitle(event: NavigationEnd): void {
    const urlToTitle = {
      dashboard: 'Dashboard',
      oms: 'OMS',
      ems: 'EMS',
      'ems-v3': 'EMS V3',
      pms: 'PMS',
      pricing: 'Pricing',
      risk: 'Risk',
      reconciliation: 'Reconciliation',
      pnl: 'P&L',
      reporting: 'Reporting',
      'shadow-nav': 'Shadow Nav',
      securities: 'Securities',
      testing: 'Testing',
      rebalancing: 'Rebalancing',
      'cash-flow': 'Cash Flow',
      'stock-loan': 'Stock Loan',
      'corporate-actions': 'Corporate actions',
      'bulk-data-upload': 'Bulk Data Upload',
      'api-playground': 'Api Playground',
    };
    let title = 'Pinnakl';
    const urlParts = event.url.split('/');
    if (urlParts.length > 1 && Object.keys(urlToTitle).includes(urlParts[1])) {
      title = `Pinnakl POEMS - ${urlToTitle[urlParts[1]]}`;
    }
    this.title.setTitle(title);
  }
}
